import { useCallback, useRef, useState } from "react";
import type { ReactNode } from "react";
import { createPortal } from "react-dom";
import clsx from "clsx";

interface TooltipProps {
  /** The text to show. Nothing renders when this is empty. */
  label?: string;
  /** Resolved on hover rather than at render, for a label that depends on
   *  laid-out geometry. TruncatedText wants a tooltip only when the text is
   *  actually clipped, and that isn't known until the browser has measured
   *  it — a `label` computed during render would always be a frame stale. */
  getLabel?: () => string | undefined;
  /** Rich content instead of a text label — chips, a small table, several
   *  styled lines. Takes precedence over `label`/`getLabel`. The bubble
   *  is otherwise the same: same placement, same theme, still inert. */
  content?: ReactNode;
  children: React.ReactNode;
  /** Wrapper class — the wrapper is inline-flex, so it takes no layout of
   *  its own beyond what the trigger already occupied. */
  className?: string;
  /** `contents` makes the wrapper generate no box at all, so wrapping an
   *  existing element in a Tooltip cannot change how it lays out. Use it
   *  where the trigger is a block, a table cell's child, or a flex item
   *  whose width the parent controls; the default inline-flex would make
   *  those shrink to fit their content instead. */
  display?: "inline-flex" | "contents";
}

type Pos = { x: number; y: number; below: boolean; text: ReactNode };

/**
 * An instant, themed tooltip.
 *
 * The native `title` attribute was doing this job, but it waits about a second
 * before appearing and can't be styled — icon-only controls (open in new tab,
 * call, WhatsApp, copy) gave no immediate explanation of what they do, and
 * text clipped to an ellipsis made you wait to find out what it said.
 *
 * Portaled to the body and positioned `fixed` from the trigger's own rect,
 * rather than absolutely inside the trigger: these controls sit in cards,
 * table cells and scroll containers that clip their overflow, which is where
 * an absolutely-positioned bubble disappears.
 *
 * Triggers on focus as well as hover, so it is reachable by keyboard.
 */
export function Tooltip({
  label,
  getLabel,
  content,
  children,
  className,
  display = "inline-flex",
}: TooltipProps) {
  const ref = useRef<HTMLSpanElement | null>(null);
  const [pos, setPos] = useState<Pos | null>(null);

  const show = useCallback(() => {
    const el = ref.current;
    if (!el) return;

    const text = content ?? (getLabel ? getLabel() : label);
    if (!text) {
      setPos(null);
      return;
    }

    // Measure the trigger, not the wrapper. When the child is absolutely
    // positioned it is out of flow — and when the wrapper is `display:
    // contents` it has no box at all — so the wrapper collapses to 0x0 and
    // the bubble would be placed at the wrong point entirely.
    let r = el.getBoundingClientRect();
    if (r.width === 0 && r.height === 0) {
      const child = el.firstElementChild;
      if (child) r = child.getBoundingClientRect();
    }
    // Flip below when there isn't room above, so the bubble never sits
    // off-screen for a control near the top of the viewport.
    const below = r.top < 44;

    setPos({
      x: r.left + r.width / 2,
      y: below ? r.bottom + 8 : r.top - 8,
      below,
      text,
    });
  }, [label, getLabel, content]);

  const hide = useCallback(() => setPos(null), []);

  if (!label && !getLabel && !content) return <>{children}</>;

  return (
    <>
      <span
        ref={ref}
        // GlobalTooltips leaves whatever is in here to this tooltip.
        data-tooltip-host=""
        className={className}
        style={{ display }}
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
      >
        {children}
      </span>

      {pos &&
        createPortal(
          <span
            role="tooltip"
            style={{
              position: "fixed",
              left: pos.x,
              top: pos.y,
              transform: pos.below
                ? "translate(-50%, 0)"
                : "translate(-50%, -100%)",
              // Rich content is a row of chips, and a status transition
              // ("Sent For External Review -> External Review Received")
              // needs the extra room to stay on one line.
              maxWidth: content
                ? "min(32rem, calc(100vw - 1rem))"
                : "min(24rem, calc(100vw - 1rem))",
            }}
            // A text label is a short hint, so it stays the compact inverted
            // bubble. Rich content (status and role chips, small tables) is a
            // popover in the page's own theme: chips are designed for the card
            // surface, and on the inverted bubble their soft tints vanished.
            className={clsx(
              "pointer-events-none z-[100001] whitespace-pre-line text-xs font-medium",
              content
                ? "rounded-xl border border-[rgb(var(--border))] bg-[rgb(var(--card))] px-3 py-2.5 text-[rgb(var(--text-primary))] shadow-lifted"
                : "rounded-md bg-[rgb(var(--tooltip-bg,var(--card)))] px-2 py-1 text-[rgb(var(--tooltip-fg,var(--text-primary)))] shadow-lg",
            )}
          >
            {pos.text}
          </span>,
          document.body,
        )}
    </>
  );
}
